import React, { useState } from 'react';
import { Plus, Eye, Edit2, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatDate, generateId } from '../../utils/helpers';

const emptyForm = {
  studentName: '',
  fatherName: '',
  motherName: '',
  phone: '',
  alternatePhone: '',
  location: '',
  classInterested: '',
  enquiryDate: new Date().toISOString().split('T')[0],
  source: '',
  status: 'new',
  notes: '',
};

const inputCls = 'w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300';
const classList = ['Nursery','LKG','UKG','Class 1','Class 2','Class 3','Class 4','Class 5','Class 6','Class 7','Class 8','Class 9','Class 10'];
const sources = ['Walk-in','Phone Call','Reference','Social Media','Advertisement','Website','Other'];

const statusColors = {
  new: 'blue',
  'follow-up': 'yellow',
  admitted: 'green',
  'not-interested': 'red',
};

function Field({ label, children, span = 1 }) {
  const spanClass = span === 2 ? 'col-span-2' : span === 3 ? 'col-span-3' : '';
  return (
    <div className={spanClass}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

function AdmissionEnquiryList() {
  const { enquiries, setEnquiries } = useApp();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'view'
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const openAdd = () => { setForm({ ...emptyForm }); setModal('add'); };
  const openEdit = (e) => { setForm(e); setSelected(e); setModal('edit'); };
  const openView = (e) => { setSelected(e); setModal('view'); };

  const save = () => {
    if (modal === 'add') {
      setEnquiries(prev => [...prev, { ...form, id: generateId('ENQ') }]);
    } else {
      setEnquiries(prev => prev.map(e => e.id === selected.id ? { ...form, id: selected.id } : e));
    }
    setModal(null);
  };

  const remove = (id) => {
    if (window.confirm('Delete this enquiry?')) setEnquiries(prev => prev.filter(e => e.id !== id));
  };

  const filtered = filterStatus === 'all' ? enquiries : enquiries.filter(e => e.status === filterStatus);

  const columns = [
    { label: '#', key: 'num', render: (_, i) => i + 1 },
    { label: 'Student', key: 'student', render: e => (
      <div>
        <p className="font-medium">{e.studentName}</p>
        <p className="text-xs text-gray-400">{e.classInterested || '—'}</p>
      </div>
    )},
    { label: 'Father', key: 'father', render: e => e.fatherName || '—' },
    { label: 'Mother', key: 'mother', render: e => e.motherName || '—' },
    { label: 'Phone', accessor: 'phone' },
    { label: 'Location', accessor: 'location', render: e => e.location || '—' },
    { label: 'Date', key: 'date', render: e => formatDate(e.enquiryDate) },
    { label: 'Source', accessor: 'source', render: e => e.source || '—' },
    { label: 'Status', key: 'status', render: e => (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
        e.status === 'admitted' ? 'bg-green-100 text-green-700' :
        e.status === 'follow-up' ? 'bg-yellow-100 text-yellow-700' :
        e.status === 'not-interested' ? 'bg-red-100 text-red-700' :
        'bg-blue-100 text-blue-700'
      }`}>
        {e.status === 'not-interested' ? 'Not Interested' : e.status.charAt(0).toUpperCase() + e.status.slice(1)}
      </span>
    )},
  ];

  const actions = (e) => (
    <div className="flex gap-1">
      <button onClick={() => openView(e)} className="p-1 text-gray-400 hover:text-indigo-600"><Eye size={15} /></button>
      <button onClick={() => openEdit(e)} className="p-1 text-gray-400 hover:text-indigo-600"><Edit2 size={15} /></button>
      <button onClick={() => remove(e.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={15} /></button>
    </div>
  );

  const stats = [
    { label: 'Total', count: enquiries.length, color: 'text-gray-700' },
    { label: 'New', count: enquiries.filter(e => e.status === 'new').length, color: 'text-blue-600' },
    { label: 'Follow-up', count: enquiries.filter(e => e.status === 'follow-up').length, color: 'text-yellow-600' },
    { label: 'Admitted', count: enquiries.filter(e => e.status === 'admitted').length, color: 'text-green-600' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Admission Enquiries</h1>
          <p className="text-gray-500 text-sm mt-0.5">Track and manage incoming admission enquiries</p>
        </div>
        <div className="flex gap-2">
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
            <Plus size={16} /> New Enquiry
          </button>
          <button onClick={() => navigate('/admission/enquiry')} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">
            Full Form
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {stats.map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4">
            <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
            <p className="text-sm text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {['all','new','follow-up','admitted','not-interested'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded-lg text-sm font-medium border transition-colors ${filterStatus === s ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
            {s === 'all' ? 'All' : s === 'not-interested' ? 'Not Interested' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <DataTable columns={columns} data={filtered} actions={actions} searchable pageSize={15} />

      {/* Add / Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'add' ? 'New Enquiry' : 'Edit Enquiry'} onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-4 p-1">
            <Field label="Student Name" span={2}>
              <input required value={form.studentName} onChange={e => set('studentName', e.target.value)} className={inputCls} placeholder="Full name" />
            </Field>
            <Field label="Father's Name">
              <input value={form.fatherName} onChange={e => set('fatherName', e.target.value)} className={inputCls} placeholder="Full name" />
            </Field>
            <Field label="Mother's Name">
              <input value={form.motherName} onChange={e => set('motherName', e.target.value)} className={inputCls} placeholder="Full name" />
            </Field>
            <Field label="Phone">
              <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} className={inputCls} placeholder="Primary phone" />
            </Field>
            <Field label="Alternate Phone">
              <input type="tel" value={form.alternatePhone} onChange={e => set('alternatePhone', e.target.value)} className={inputCls} placeholder="Optional" />
            </Field>
            <Field label="Location" span={2}>
              <input value={form.location} onChange={e => set('location', e.target.value)} className={inputCls} placeholder="e.g. Hyderabad, Miyapur" />
            </Field>
            <Field label="Class Interested">
              <select value={form.classInterested} onChange={e => set('classInterested', e.target.value)} className={inputCls}>
                <option value="">Select</option>
                {classList.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Source">
              <select value={form.source} onChange={e => set('source', e.target.value)} className={inputCls}>
                <option value="">Select</option>
                {sources.map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Enquiry Date">
              <input type="date" value={form.enquiryDate} onChange={e => set('enquiryDate', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls}>
                {[['new','New'],['follow-up','Follow-up'],['admitted','Admitted'],['not-interested','Not Interested']].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </Field>
            <Field label="Notes" span={2}>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} className={inputCls} placeholder="Additional notes..." />
            </Field>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Save</button>
          </div>
        </Modal>
      )}

      {/* View Modal */}
      {modal === 'view' && selected && (
        <Modal title="Enquiry Details" onClose={() => setModal(null)}>
          <div className="space-y-3 text-sm p-1">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              <div><span className="text-gray-500">Student Name</span><p className="font-medium">{selected.studentName}</p></div>
              <div><span className="text-gray-500">Class Interested</span><p className="font-medium">{selected.classInterested || '—'}</p></div>
              <div><span className="text-gray-500">Father's Name</span><p className="font-medium">{selected.fatherName || '—'}</p></div>
              <div><span className="text-gray-500">Mother's Name</span><p className="font-medium">{selected.motherName || '—'}</p></div>
              <div><span className="text-gray-500">Phone</span><p className="font-medium">{selected.phone}</p></div>
              <div><span className="text-gray-500">Alternate Phone</span><p className="font-medium">{selected.alternatePhone || '—'}</p></div>
              <div><span className="text-gray-500">Location</span><p className="font-medium">{selected.location || '—'}</p></div>
              <div><span className="text-gray-500">Source</span><p className="font-medium">{selected.source || '—'}</p></div>
              <div><span className="text-gray-500">Enquiry Date</span><p className="font-medium">{formatDate(selected.enquiryDate)}</p></div>
              <div><span className="text-gray-500">Status</span>
                <p className="font-medium capitalize">{selected.status === 'not-interested' ? 'Not Interested' : selected.status}</p>
              </div>
            </div>
            {selected.notes && (
              <div className="border-t pt-3">
                <p className="text-gray-500 mb-1">Notes</p>
                <p>{selected.notes}</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Close</button>
            <button onClick={() => openEdit(selected)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Edit</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AdmissionEnquiryList;
